import type { ExportValue } from './table-export.ts'

/**
 * Uma planilha `.xlsx` de uma aba, escrita sem biblioteca — F131.
 *
 * O `.xlsx` é um zip de meia dúzia de XMLs. A exportação precisa de uma aba,
 * um cabeçalho em negrito e células de texto ou número; uma biblioteca de
 * planilha inteira (centenas de KB no pacote do navegador) para isso seria o
 * mesmo erro que `@aeris/import` recusou do lado da leitura.
 *
 * O zip é gravado **sem compressão** (método 0, "store"): o Excel, o LibreOffice
 * e o Google Planilhas abrem, e não há algoritmo de compressão para escrever
 * nem para errar. O arquivo fica maior, e 5 mil linhas continuam sendo poucos
 * megabytes.
 *
 * Referência do formato: ECMA-376 (Office Open XML), partes 1 e 2.
 */

// ---------------------------------------------------------------------------
// zip
// ---------------------------------------------------------------------------

const CRC_TABLE = ((): Uint32Array => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n += 1) {
    let c = n
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) === 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[n] = c >>> 0
  }
  return table
})()

export const crc32 = (bytes: Uint8Array): number => {
  let crc = 0xffffffff
  for (const byte of bytes) {
    crc = (CRC_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

type ZipFile = { readonly name: string; readonly data: Uint8Array }

/** 1º de janeiro de 1980, 00:00 — a data mínima do formato DOS. */
const DOS_DATE = 0x21
const DOS_TIME = 0
/** Bit 11: nomes em UTF-8. */
const UTF8_FLAG = 0x0800

export const zipStore = (
  files: readonly ZipFile[]
): Uint8Array<ArrayBuffer> => {
  const encoder = new TextEncoder()
  const entries = files.map((file) => ({
    name: encoder.encode(file.name),
    data: file.data,
    crc: crc32(file.data),
  }))

  const localSize = entries.reduce(
    (sum, entry) => sum + 30 + entry.name.length + entry.data.length,
    0
  )
  const centralSize = entries.reduce(
    (sum, entry) => sum + 46 + entry.name.length,
    0
  )
  const out = new Uint8Array(localSize + centralSize + 22)
  const view = new DataView(out.buffer)
  let at = 0

  const u16 = (value: number): void => {
    view.setUint16(at, value, true)
    at += 2
  }
  const u32 = (value: number): void => {
    view.setUint32(at, value, true)
    at += 4
  }
  const bytes = (value: Uint8Array): void => {
    out.set(value, at)
    at += value.length
  }

  const offsets: number[] = []
  for (const entry of entries) {
    offsets.push(at)
    u32(0x04034b50)
    u16(20)
    u16(UTF8_FLAG)
    u16(0)
    u16(DOS_TIME)
    u16(DOS_DATE)
    u32(entry.crc)
    u32(entry.data.length)
    u32(entry.data.length)
    u16(entry.name.length)
    u16(0)
    bytes(entry.name)
    bytes(entry.data)
  }

  const centralStart = at
  entries.forEach((entry, index) => {
    u32(0x02014b50)
    u16(20)
    u16(20)
    u16(UTF8_FLAG)
    u16(0)
    u16(DOS_TIME)
    u16(DOS_DATE)
    u32(entry.crc)
    u32(entry.data.length)
    u32(entry.data.length)
    u16(entry.name.length)
    u16(0)
    u16(0)
    u16(0)
    u16(0)
    u32(0)
    u32(offsets[index] ?? 0)
    bytes(entry.name)
  })

  // Medido ANTES de escrever o registro de fim: depois, `at` já andou os
  // bytes dele e o tamanho sairia 12 a mais — o Excel recusa o arquivo.
  const centralLength = at - centralStart

  u32(0x06054b50)
  u16(0)
  u16(0)
  u16(entries.length)
  u16(entries.length)
  u32(centralLength)
  u32(centralStart)
  u16(0)

  return out
}

// ---------------------------------------------------------------------------
// planilha
// ---------------------------------------------------------------------------

/**
 * Texto dentro de XML. Os caracteres de controle que o XML 1.0 não aceita são
 * retirados: um cadastro importado com um caractere de controle perdido faria o Excel dizer
 * que o arquivo está corrompido, e não em qual linha.
 */
/**
 * O que o XML 1.0 não aceita: controles de U+0000 a U+001F (menos tabulação,
 * quebra de linha e retorno) e U+FFFE/U+FFFF. Montado por código, e não com
 * escapes num literal, para nenhuma ferramenta trocá-los por caracteres
 * invisíveis no arquivo.
 */
const INVALID_XML = new RegExp(
  `[${String.fromCharCode(0)}-${String.fromCharCode(8)}${String.fromCharCode(11, 12)}${String.fromCharCode(14)}-${String.fromCharCode(31)}${String.fromCharCode(0xfffe, 0xffff)}]`,
  'g'
)

export const xmlText = (value: string): string =>
  value
    .replace(INVALID_XML, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

/** 0 → `A`, 25 → `Z`, 26 → `AA`. */
export const columnName = (index: number): string => {
  let name = ''
  let rest = index + 1
  while (rest > 0) {
    const letter = (rest - 1) % 26
    name = String.fromCharCode(65 + letter) + name
    rest = Math.floor((rest - 1) / 26)
  }
  return name
}

/** Só o que o decimal da API pode ser — o resto vai como texto, nunca quebra o arquivo. */
const NUMERIC = /^-?\d+(\.\d+)?$/

const cellXml = (
  value: ExportValue,
  reference: string,
  style: number
): string => {
  const styleAttribute = style === 0 ? '' : ` s="${String(style)}"`
  if (value === null) return ''
  if (
    typeof value !== 'string' &&
    'number' in value &&
    NUMERIC.test(value.number)
  ) {
    return `<c r="${reference}"${styleAttribute}><v>${value.number}</v></c>`
  }
  // A data chega já escrita para leitura (`toGrid`); se vier crua, vai o ISO.
  const text =
    typeof value === 'string'
      ? value
      : 'number' in value
        ? value.number
        : value.date
  return `<c r="${reference}"${styleAttribute} t="inlineStr"><is><t xml:space="preserve">${xmlText(text)}</t></is></c>`
}

/** Até 31 caracteres, sem `[]:*?/\` — as regras do Excel para nome de aba. */
export const sheetTitle = (name: string): string => {
  const cleaned = name.replace(/[[\]:*?/\\]/g, ' ').trim()
  return (cleaned === '' ? 'Planilha' : cleaned).slice(0, 31)
}

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`

const WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`

/** Estilo 0 é o normal; estilo 1 é o cabeçalho em negrito. */
const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`

const workbook = (title: string): string =>
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${xmlText(title)}" sheetId="1" r:id="rId1"/></sheets></workbook>`

const worksheet = (
  header: readonly string[],
  rows: readonly (readonly ExportValue[])[]
): string => {
  const lines: string[] = []
  lines.push(
    `<row r="1">${header
      .map((label, index) => cellXml(label, `${columnName(index)}1`, 1))
      .join('')}</row>`
  )
  rows.forEach((row, rowIndex) => {
    const number = String(rowIndex + 2)
    lines.push(
      `<row r="${number}">${row
        .map((value, index) =>
          cellXml(value, `${columnName(index)}${number}`, 0)
        )
        .join('')}</row>`
    )
  })

  // O cabeçalho fica parado ao rolar: com 5 mil linhas, ninguém lembra qual
  // coluna era o preço.
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><sheetData>${lines.join('')}</sheetData></worksheet>`
}

export const xlsxBytes = (
  sheetName: string,
  header: readonly string[],
  rows: readonly (readonly ExportValue[])[]
): Uint8Array<ArrayBuffer> => {
  const encoder = new TextEncoder()
  return zipStore([
    { name: '[Content_Types].xml', data: encoder.encode(CONTENT_TYPES) },
    { name: '_rels/.rels', data: encoder.encode(ROOT_RELS) },
    {
      name: 'xl/workbook.xml',
      data: encoder.encode(workbook(sheetTitle(sheetName))),
    },
    { name: 'xl/_rels/workbook.xml.rels', data: encoder.encode(WORKBOOK_RELS) },
    { name: 'xl/styles.xml', data: encoder.encode(STYLES) },
    {
      name: 'xl/worksheets/sheet1.xml',
      data: encoder.encode(worksheet(header, rows)),
    },
  ])
}
